import type {
  GetIntegrationSecretsParams,
  GetIntegrationSecretsResponse,
  IntegrationSecret,
} from '@kizen/api/integration-secrets';
import type {
  DefaultError,
  InfiniteData,
  InfiniteQueryObserverOptions,
  QueryObserverOptions,
} from '@tanstack/query-core';
import type { AxiosInstance } from 'axios';
import type { Resource } from './resource';
import type {
  Drop,
  WithInfiniteSelect,
  WithParams,
  WithRequired,
} from './types';

import {
  getIntegrationSecret,
  getIntegrationSecrets,
} from '@kizen/api/integration-secrets';
import { getResource } from './resource';
import { curry1 } from './utils';

type IntegrationSecretQuery<TData = IntegrationSecret> = WithRequired<
  QueryObserverOptions<
    IntegrationSecret,
    DefaultError,
    TData,
    IntegrationSecret,
    [Resource<Readonly<['integration-secrets', string]>>]
  >,
  'queryFn' | 'queryKey'
>;

export const integrationSecretQuery = <TData = IntegrationSecret>(
  axios: AxiosInstance,
  id: string,
  options?: Omit<IntegrationSecretQuery<TData>, 'queryFn' | 'queryKey'>
): IntegrationSecretQuery<TData> => {
  const path = ['integration-secrets', id] as const;
  const resource = getResource(path);

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;

      return getIntegrationSecret(axios, qk.path[1], { signal });
    },
    ...options,
  };
};

type IntegrationSecretsQuery<TData = GetIntegrationSecretsResponse> =
  WithRequired<
    QueryObserverOptions<
      GetIntegrationSecretsResponse,
      DefaultError,
      TData,
      GetIntegrationSecretsResponse,
      [
        Resource<
          Readonly<['integration-secrets']>,
          GetIntegrationSecretsParams
        >,
      ]
    >,
    'queryFn' | 'queryKey'
  >;

export const integrationSecretsQuery = <
  TData = GetIntegrationSecretsResponse,
>(
  axios: AxiosInstance,
  options: WithParams<
    Omit<IntegrationSecretsQuery<TData>, 'queryFn' | 'queryKey'>,
    GetIntegrationSecretsParams
  > = {}
): IntegrationSecretsQuery<TData> => {
  const path = ['integration-secrets'] as const;
  const { params, ...queryOpts } = options;
  const resource = getResource(path, { params });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;

      return getIntegrationSecrets(axios, {
        params: { ordering: 'description', ...qk.params },
        signal,
      });
    },
    ...queryOpts,
  };
};

type PageParam = string | undefined;

type IntegrationSecretsInfiniteQuery<
  TData = InfiniteData<GetIntegrationSecretsResponse, PageParam>,
> = WithRequired<
  WithInfiniteSelect<
    InfiniteQueryObserverOptions<
      GetIntegrationSecretsResponse,
      DefaultError,
      TData,
      GetIntegrationSecretsResponse,
      [
        Resource<
          Readonly<['integration-secrets']>,
          GetIntegrationSecretsParams
        >,
      ],
      PageParam
    >
  >,
  'queryFn' | 'queryKey'
>;

type IntegrationSecretsInfiniteQueryOptions<TData> = WithParams<
  Omit<
    IntegrationSecretsInfiniteQuery<TData>,
    | 'queryFn'
    | 'queryKey'
    | 'getNextPageParam'
    | 'getPreviousPageParam'
    | 'initialPageParam'
  >,
  GetIntegrationSecretsParams
>;

export const integrationSecretsInfiniteQuery = <
  TData = InfiniteData<GetIntegrationSecretsResponse, PageParam>,
>(
  axios: AxiosInstance,
  options: IntegrationSecretsInfiniteQueryOptions<TData> = {}
): IntegrationSecretsInfiniteQuery<TData> => {
  const path = ['integration-secrets'] as const;
  const { params, ...queryOpts } = options;
  const resource = getResource(path, { infinite: true, params });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        pageParam,
        queryKey: [qk],
        signal,
      } = context;

      return getIntegrationSecrets(axios, {
        signal,
        params: {
          ...qk.params,
          page: pageParam ? Number(pageParam) : 1,
        },
      });
    },
    getNextPageParam: (page) =>
      page.next
        ? new URL(page.next).searchParams.get('page') ?? undefined
        : undefined,
    getPreviousPageParam: (page) =>
      page.previous
        ? new URL(page.previous).searchParams.get('page') ?? undefined
        : undefined,
    initialPageParam: undefined,
    ...queryOpts,
  };
};

class Queries {
  public integrationSecretQuery: <TData = IntegrationSecret>(
    ...args: Drop<Parameters<typeof integrationSecretQuery<TData>>>
  ) => IntegrationSecretQuery<TData>;
  public integrationSecretsQuery: <TData = GetIntegrationSecretsResponse>(
    ...args: Drop<Parameters<typeof integrationSecretsQuery<TData>>>
  ) => IntegrationSecretsQuery<TData>;
  public integrationSecretsInfiniteQuery: <
    TData = InfiniteData<GetIntegrationSecretsResponse, PageParam>,
  >(
    ...args: Drop<Parameters<typeof integrationSecretsInfiniteQuery<TData>>>
  ) => ReturnType<typeof integrationSecretsInfiniteQuery<TData>>;

  constructor(axios: AxiosInstance) {
    this.integrationSecretQuery = curry1(integrationSecretQuery)(axios);
    this.integrationSecretsQuery = curry1(integrationSecretsQuery)(axios);
    this.integrationSecretsInfiniteQuery = curry1(
      integrationSecretsInfiniteQuery
    )(axios);
  }
}

export type IntegrationSecretQueries = Queries;

export const defIntegrationSecretQueries = (
  axios: AxiosInstance
): IntegrationSecretQueries => {
  return new Queries(axios);
};
